import { useState } from 'react';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { DateField } from '@/kit/DateField';
import { Select, SelectOption } from '@/kit/Select';
import { SelectionBar } from '@/kit/SelectionBar';
import type { Dayjs } from 'dayjs';
import { notifyError } from '@/core/notify/notify';
import { dateRangeProblem } from '@/core/utils/dates';
import { selectedClient } from '@/core/selectedClient';
import { reportsService } from '@/core/services/reports';
import { ExportFormatCode, toIsoDate } from '@/core/models/practice.models';
import type { EmailLogQuery, EmailLogRow } from '@/core/models/reports.models';
import { ExportMenu, formatDate, SearchField, useDebouncedDistinct, useLiveState, useOnInit } from './shared';
import './Logs.scss';

interface State {
  dataSource: EmailLogRow[];
  /** True once a list has come back, so the empty message is not shown while loading. */
  loaded: boolean;
  selection: ReadonlySet<string>;
  search: string;
  /** A string `'0'`..`'8'`, converted with `Number()` when the query is built; '8' is the custom From/To range. */
  filter: string;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
}

/** BUG-060: the filter value of the custom From/To range. */
const CUSTOM = '8';

/**
 * Log of every email the application attempted to send (Angular TaxreturnLogsEmaillogsComponent). BUG-059: with a client
 * open it lists only that client's emails; without one it is the firm-wide log.
 */
export function TaxreturnLogsEmaillogs() {
  const [s, set, live] = useLiveState<State>({
    dataSource: [],
    loaded: false,
    selection: new Set(),
    search: '',
    filter: '0',
    fromDate: null,
    toDate: null,
  });
  const [clientId] = useState(() => selectedClient()?.id ?? null);
  const onSearch = useDebouncedDistinct(400, () => load());

  useOnInit(() => load());

  function query(): EmailLogQuery {
    const filter = Number(live.current.filter);
    const custom = live.current.filter === CUSTOM;
    return {
      search: live.current.search?.trim() || null,
      filter: Number.isNaN(filter) ? 0 : filter,
      ...(custom ? { fromDate: toIsoDate(live.current.fromDate), toDate: toIsoDate(live.current.toDate) } : {}),
      ...(clientId ? { clientId } : {}),
    };
  }

  /** The message for a custom range that runs backwards, else null: such a range is never searched. */
  function rangeProblem(): string | null {
    const { filter, fromDate, toDate } = live.current;
    return filter === CUSTOM ? dateRangeProblem(fromDate, toDate) : null;
  }

  function load(): void {
    if (rangeProblem()) {
      return;
    }
    reportsService.getEmailLogs(query()).then(
      (rows) => set({ dataSource: rows, loaded: true, selection: new Set() }),
      (error) => notifyError(error),
    );
  }

  const isAllSelected = () => s.dataSource.length > 0 && s.dataSource.every((r) => s.selection.has(r.id));
  const isPartlySelected = () => s.selection.size > 0 && !isAllSelected();

  const toggleAll = (checked: boolean) =>
    set((current) => ({ selection: new Set(checked ? current.dataSource.map((r) => r.id) : []) }));

  const toggleRow = (row: EmailLogRow, checked: boolean) =>
    set((current) => {
      const selection = new Set(current.selection);
      if (checked) {
        selection.add(row.id);
      } else {
        selection.delete(row.id);
      }
      return { selection };
    });

  function exportLogs(format: ExportFormatCode): void {
    const problem = rangeProblem();
    if (problem) {
      notifyError(problem);
      return;
    }
    reportsService
      .exportEmailLogs(query(), format, Array.from(live.current.selection))
      .then(undefined, (error) => notifyError(error));
  }

  const columns: DataTableColumn<EmailLogRow>[] = [
    {
      id: 'date',
      header: (
        <>
          <Checkbox
            checked={isAllSelected()}
            indeterminate={isPartlySelected()}
            onChange={(event) => toggleAll(event.target.checked)}
          />
          Date
        </>
      ),
      cell: (row) => (
        <>
          <Checkbox checked={s.selection.has(row.id)} onChange={(event) => toggleRow(row, event.target.checked)} />
          {formatDate(row.createdAtUtc, 'DD-MMM-YYYY hh:mm:ss A')}
        </>
      ),
    },
    { id: 'sent', header: 'Sent to', cell: (row) => row.sentTo },
    { id: 'subject', header: 'Subject', cell: (row) => row.subject },
    { id: 'attachment', header: 'Attachment', cell: (row) => row.attachment },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => (
        <span
          className={
            [row.status === 'Sent' && 'text-green', row.status === 'Failed' && 'text-red'].filter(Boolean).join(' ') ||
            undefined
          }
        >
          {row.status}
        </span>
      ),
    },
    { id: 'user', header: 'User', cell: (row) => row.user },
    { id: 'view', header: 'View', cell: (row) => row.view },
  ];

  return (
    <SideMenuPage menu="logs" contentClassName="tr-logs">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <SearchField
              value={s.search}
              onSearch={(value) => {
                set({ search: value });
                onSearch(value ?? '');
              }}
            />
          </div>

          <div className="content-header-right mb-1">
            {/* BUG-060: the custom range only shows its dates once Custom is chosen. */}
            {s.filter === CUSTOM && (
              <>
                <DateField
                  fieldClassName="w-150"
                  placeholder="From"
                  invalid={dateRangeProblem(s.fromDate, s.toDate) !== null}
                  value={s.fromDate}
                  onChange={(value) => {
                    set({ fromDate: value });
                    load();
                  }}
                />
                <DateField
                  fieldClassName="w-150"
                  placeholder="To"
                  min={s.fromDate ?? undefined}
                  invalid={dateRangeProblem(s.fromDate, s.toDate) !== null}
                  value={s.toDate}
                  onChange={(value) => {
                    set({ toDate: value });
                    load();
                  }}
                />
              </>
            )}
            <Select
              className="w-150"
              openOnFocus
              panelClass="myPanelClass"
              clearable={false}
              value={s.filter}
              onChange={(value) => {
                set({ filter: value as string });
                load();
              }}
            >
              <SelectOption value="0">Last 30 days</SelectOption>
              <SelectOption value="1">Last Week</SelectOption>
              <SelectOption value="2">This Month</SelectOption>
              <SelectOption value="3">Last Month</SelectOption>
              <SelectOption value="4">This Quarter</SelectOption>
              <SelectOption value="5">Last Quarter</SelectOption>
              <SelectOption value="6">This Year</SelectOption>
              <SelectOption value="7">Last Year</SelectOption>
              <SelectOption value={CUSTOM}>Custom</SelectOption>
            </Select>
            <ExportMenu onExport={exportLogs} />
          </div>
        </div>
        {s.filter === CUSTOM && dateRangeProblem(s.fromDate, s.toDate) && (
          <p className="text-danger mb-1">{dateRangeProblem(s.fromDate, s.toDate)}</p>
        )}
        {/* Logs are a record, never deleted: the ticked entries can be exported. */}
        <SelectionBar
          count={s.selection.size}
          onExport={() => exportLogs(ExportFormatCode.Excel)}
          onClear={() => toggleAll(false)}
        />
        <div className="mat-table-wrapper no-hover">
          <DataTable stickyHeader columns={columns} rows={s.dataSource} rowKey={(row) => row.id} />
          {s.loaded && s.dataSource.length === 0 && (
            <div className="text-align-center p-1">No Email log to display</div>
          )}
        </div>
      </div>
    </SideMenuPage>
  );
}
